// Stampa automatica delle comande sulle stampanti delle postazioni.
//
// Dopo l'invio di un ordine, il dispositivo "reclama" le comande in attesa e le
// stampa via ePOS-Print. Ogni stazione (cucina/bar) usa la propria stampante
// Wi-Fi se configurata; altrimenti la comanda viene stampata sulla STAMPANTE
// PRINCIPALE (fallback), così anche i setup con una sola stampante funzionano.

import api from './api';
import { eposPrintComanda } from './eposPrint';

const COMANDA_STATIONS = ['cucina', 'bar'];

/** ID stabile del dispositivo (per il claim delle comande). */
function deviceId() {
  let id = localStorage.getItem('rmp_device_id');
  if (!id) {
    id = 'dev-' + Math.random().toString(36).slice(2, 10);
    localStorage.setItem('rmp_device_id', id);
  }
  return id;
}

// Cache breve della configurazione di stampa (30s).
let _cfgCache = { at: 0, data: null };
async function printConfig() {
  const now = Date.now();
  if (_cfgCache.data && now - _cfgCache.at < 30000) return _cfgCache.data;
  let data = null;
  try { ({ data } = await api.get('/print/settings')); } catch { data = null; }
  _cfgCache = { at: now, data };
  return data;
}

export function clearStationsCache() { _cfgCache = { at: 0, data: null }; }

// Cooldown per-stazione (per questo dispositivo). Se il dispositivo NON riesce a
// raggiungere la stampante di una stazione (rete/certificato diversi), smette di
// reclamare le comande di quella stazione per un breve periodo: così un altro
// dispositivo, sulla rete giusta, le prende e stampa senza contese. Evita il caso
// "a volte la comanda non esce" causato da device che rubano il job e falliscono.
const STATION_COOLDOWN_MS = 30000;
const _stationCooldown = {}; // { cucina: epochMs_fino_a_quando_saltare }
function stationOnCooldown(st) {
  return Date.now() < (_stationCooldown[st] || 0);
}
function setStationCooldown(st) {
  _stationCooldown[st] = Date.now() + STATION_COOLDOWN_MS;
}

/** Stampante utilizzabile (ha un host ePOS) a partire da una config. */
function usable(cfg) {
  return cfg && cfg.host && (cfg.mode === 'device' || cfg.mode === 'wifi' || !cfg.mode);
}

/** Config ePOS pronta per eposPrintComanda. */
function toCfg(c) {
  return { host: c.host, protocol: c.protocol || 'https', port: c.port || 0, paper_width: c.paper_width || 80 };
}

/**
 * Reclama e stampa le comande in attesa. Sicura da chiamare sempre (no-op se non
 * c'è nessuna stampante configurata). Protetta contro esecuzioni sovrapposte.
 */
let _claimRunning = false;
export async function claimAndPrintComande() {
  if (_claimRunning) return;
  _claimRunning = true;
  try {
    const data = await printConfig();
    if (!data) return;
    const main = data.printer || {};
    const sp = data.station_printers || {};
    const mainUsable = usable(main);

    // Risolve la stampante target per una stazione: prima quella di postazione, poi la principale.
    const targetFor = (station) => {
      const c = sp[station];
      if (c && c.enabled && c.mode === 'wifi' && c.host) return toCfg(c);
      // Stazione gestita da un canale NON-browser (SDP: la stampante Epson stampa
      // da sola / local: stampa dal dispositivo di quella postazione / agent:
      // la stampa l'Agente PC sulla sua stampante dedicata): il browser NON deve
      // stamparla né ripiegare sulla principale (evita doppioni).
      if (c && c.enabled && (c.mode === 'sdp' || c.mode === 'local' || c.mode === 'agent')) return null;
      if (mainUsable) return toCfg(main);
      return null;
    };

    // Reclama solo le stazioni per cui esiste una stampante (di postazione o
    // principale) E che non sono in cooldown su questo dispositivo (un fallimento
    // di rete recente fa saltare la stazione così la stampa un altro dispositivo).
    const stations = COMANDA_STATIONS.filter((st) => targetFor(st) && !stationOnCooldown(st));
    if (!stations.length) return;

    let res;
    try {
      res = await api.post('/print/comande/claim', { device_id: deviceId(), stations });
    } catch { return; }
    const jobs = res?.data?.jobs || [];
    for (const job of jobs) {
      const target = targetFor(job.station);
      if (!target) {
        try { await api.post(`/print/comande/${job.id}/done`, { status: 'error', error: 'no-printer' }); } catch { /* */ }
        continue;
      }
      try {
        await eposPrintComanda(target, job.payload);
        await api.post(`/print/comande/${job.id}/done`, { status: 'printed' });
      } catch (e) {
        // Errore di RETE (stampante non raggiungibile da questo dispositivo):
        //  - mettiamo la stazione in cooldown (non la reclamiamo per un po');
        //  - segnaliamo `unreachable` al backend che rilascia il job SENZA
        //    consumare i tentativi, così un altro dispositivo lo stampa.
        const unreachable = e && e.kind === 'network';
        if (unreachable) setStationCooldown(job.station);
        try {
          await api.post(`/print/comande/${job.id}/done`, {
            status: 'error',
            error: String(e?.message || e),
            unreachable: !!unreachable,
          });
        } catch { /* */ }
        // Se la stampante non è raggiungibile, inutile insistere sugli altri job
        // della stessa tornata: interrompiamo e lasciamo fare a un altro device.
        if (unreachable) break;
      }
    }
  } finally {
    _claimRunning = false;
  }
}
